import { Check, Loader2 } from "lucide-react";

export type StepStatus = "locked" | "ready" | "done" | "active";

export interface StepCta {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  variant?: "wing" | "bnb";
  loading?: boolean;
}

interface StepCardProps {
  index: 0 | 1 | 2 | 3;
  title: string;
  status: StepStatus;
  hint?: string;
  cta?: StepCta;
}

const badgeClass: Record<StepStatus, string> = {
  done: "bg-wing-grad text-black shadow-wing",
  active: "ring-1 ring-wing/60 text-wing animate-pulse bg-wing/10",
  ready: "ring-1 ring-wing/40 text-ink bg-white/[0.03]",
  locked: "bg-white/[0.04] text-ink-dim/60",
};

const statusLabel: Record<StepStatus, string> = {
  done: "已完成",
  active: "进行中",
  ready: "等你操作",
  locked: "未开放",
};

const statusDotClass: Record<StepStatus, string> = {
  done: "bg-leaf",
  active: "bg-wing animate-pulse",
  ready: "bg-wing/60",
  locked: "bg-ink-dim/30",
};

export function StepCard({ index, title, status, hint, cta }: StepCardProps) {
  const indexLabel = String(index + 1).padStart(2, "0");
  const ctaVariant = cta?.variant ?? "wing";
  const btnClass = ctaVariant === "bnb" ? "btn-bnb" : "btn-primary";

  return (
    <article className="glass p-4 flex flex-col gap-3 min-h-[160px] transition duration-200 hover:border-white/[0.1]">
      <div className="flex items-center gap-3">
        <div
          className={`w-8 h-8 shrink-0 rounded-full flex items-center justify-center font-mono text-xs font-semibold transition duration-200 ${badgeClass[status]}`}
          aria-label={`步骤 ${indexLabel} ${statusLabel[status]}`}
        >
          {status === "done" ? (
            <Check className="w-4 h-4" strokeWidth={2.25} aria-hidden />
          ) : (
            indexLabel
          )}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-ink leading-tight">{title}</h3>
          <div className="mt-0.5 flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-wider text-ink-dim">
            <span className={`inline-block w-1.5 h-1.5 rounded-full ${statusDotClass[status]}`} />
            <span>{statusLabel[status]}</span>
          </div>
        </div>
      </div>

      {hint ? (
        <p className="text-xs text-ink-dim leading-relaxed">{hint}</p>
      ) : (
        <div className="flex-1" />
      )}

      {cta ? (
        <button
          type="button"
          onClick={cta.onClick}
          disabled={cta.disabled || cta.loading}
          className={`${btnClass} w-full inline-flex items-center justify-center gap-2 duration-150`}
        >
          {cta.loading ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2} aria-hidden />
              <span>处理中</span>
            </>
          ) : (
            <span className="truncate">{cta.label}</span>
          )}
        </button>
      ) : null}
    </article>
  );
}
